import { Form, Head, router } from "@inertiajs/react"
import { Trash2 } from "lucide-react"
import { useState } from "react"

import Heading from "@/components/heading"
import InputError from "@/components/input-error"
import { Button } from "@/components/ui/button"
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card"
import { Checkbox } from "@/components/ui/checkbox"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Spinner } from "@/components/ui/spinner"
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group"
import AppLayout from "@/layouts/app-layout"
import { appPath, editAppPath, serverPath, serversPath } from "@/routes"
import type { BreadcrumbItem } from "@/types"
import type { App, Server } from "@/types"

interface AppsEditProps {
  app: App & { server: Pick<Server, "id" | "name"> }
}

export default function AppsEdit({ app }: AppsEditProps) {
  const [appType, setAppType] = useState<"static" | "rails">(
    app.app_type || "rails",
  )

  const seoConfig = app.seo_config || { enabled: false, severity: "warning", checks: {} }
  const [seoEnabled, setSeoEnabled] = useState<boolean>(seoConfig.enabled || false)
  const [seoSeverity, setSeoSeverity] = useState<string>(seoConfig.severity || "warning")
  const [seoChecks, setSeoChecks] = useState<Record<string, boolean>>({
    robots_txt: seoConfig.checks?.robots_txt !== false,
    meta_robots: seoConfig.checks?.meta_robots !== false,
    x_robots_tag: seoConfig.checks?.x_robots_tag !== false,
    title: seoConfig.checks?.title !== false,
    meta_description: seoConfig.checks?.meta_description !== false,
    canonical: seoConfig.checks?.canonical !== false,
    open_graph: seoConfig.checks?.open_graph !== false,
    sitemap: seoConfig.checks?.sitemap !== false,
    structured_data: seoConfig.checks?.structured_data !== false,
  })

  const breadcrumbs: BreadcrumbItem[] = [
    { title: "Servers", href: serversPath() },
    { title: app.server.name, href: serverPath(app.server.id) },
    { title: app.name, href: appPath(app.id) },
    { title: "Edit", href: editAppPath(app.id) },
  ]

  function handleDelete() {
    if (
      confirm(
        `Are you sure you want to delete "${app.name}"? This will also delete all backups.`,
      )
    ) {
      router.delete(appPath(app.id))
    }
  }

  const handleAppTypeChange = (value: string) => {
    if (!value) return
    setAppType(value as "static" | "rails")
  }

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title={`Edit ${app.name}`} />

      <div className="p-6">
        <Heading
          title={`Edit ${app.name}`}
          description="Update application configuration"
        />

        <Card className="mt-6 max-w-2xl">
          <CardHeader>
            <CardTitle>Application Details</CardTitle>
            <CardDescription>
              Update the basic information about your application
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Form method="put" action={appPath(app.id)} className="space-y-6">
              {({ processing, errors }) => (
                <>
                  <div className="grid gap-2">
                    <Label htmlFor="app_type">Application Type *</Label>
                    <input type="hidden" name="app[app_type]" value={appType} />
                    <ToggleGroup
                      type="single"
                      value={appType}
                      onValueChange={handleAppTypeChange}
                      variant="outline"
                      className="w-full justify-start"
                    >
                      <ToggleGroupItem value="rails" className="flex-1">
                        Rails
                      </ToggleGroupItem>
                      <ToggleGroupItem value="static" className="flex-1">
                        Static
                      </ToggleGroupItem>
                    </ToggleGroup>
                    <p className="text-muted-foreground text-xs">
                      {appType === "rails"
                        ? "Ruby on Rails application with database and environment settings"
                        : "Static site (NextJS, HTML, etc.) without database requirements"}
                    </p>
                    <InputError messages={errors["app.app_type"]} />
                  </div>

                  <div className="grid gap-2">
                    <Label htmlFor="name">Name *</Label>
                    <Input
                      id="name"
                      name="app[name]"
                      type="text"
                      required
                      defaultValue={app.name}
                    />
                    <InputError messages={errors["app.name"]} />
                  </div>

                  <div className="grid gap-2">
                    <Label htmlFor="location">Location</Label>
                    <Input
                      id="location"
                      name="app[location]"
                      type="text"
                      defaultValue={app.location || ""}
                      placeholder="/var/www/myapp"
                    />
                    <p className="text-muted-foreground text-xs">
                      The local path to the application on the server
                    </p>
                    <InputError messages={errors["app.location"]} />
                  </div>

                  <div className="grid gap-2">
                    <Label htmlFor="app_url">Primary URL</Label>
                    <Input
                      id="app_url"
                      name="app[app_url]"
                      type="url"
                      defaultValue={app.app_url || ""}
                      placeholder="https://myapp.example.com"
                    />
                    <InputError messages={errors["app.app_url"]} />
                  </div>

                  <div className="grid gap-2">
                    <Label htmlFor="other_urls">Other URLs</Label>
                    <Input
                      id="other_urls"
                      name="app[other_urls]"
                      type="text"
                      defaultValue={app.other_urls || ""}
                      placeholder="https://staging.example.com, https://api.example.com"
                    />
                    <p className="text-muted-foreground text-xs">
                      Comma-separated list of additional URLs
                    </p>
                    <InputError messages={errors["app.other_urls"]} />
                  </div>

                  <div className="grid gap-2">
                    <Label htmlFor="health_urls">Health Check URLs</Label>
                    <Input
                      id="health_urls"
                      name="app[health_urls]"
                      type="text"
                      defaultValue={app.health_urls || ""}
                      placeholder="https://myapp.example.com/health"
                    />
                    <p className="text-muted-foreground text-xs">
                      Comma-separated URLs to monitor for health
                    </p>
                    <InputError messages={errors["app.health_urls"]} />
                  </div>

                  {appType === "rails" && (
                    <>
                      <div className="grid gap-2">
                        <Label htmlFor="environment">Environment</Label>
                        <input
                          id="environment"
                          name="app[environment]"
                          type="text"
                          list="environment-options"
                          defaultValue={app.environment || ""}
                          placeholder="development, staging, production..."
                          className="border-input bg-background ring-offset-background placeholder:text-muted-foreground focus-visible:ring-ring flex h-10 w-full rounded-md border px-3 py-2 text-sm focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none"
                        />
                        <datalist id="environment-options">
                          <option value="development" />
                          <option value="staging" />
                          <option value="production" />
                        </datalist>
                        <p className="text-muted-foreground text-xs">
                          The deployment environment for this application
                        </p>
                        <InputError messages={errors["app.environment"]} />
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        <div className="grid gap-2">
                          <Label htmlFor="db_type">Database Type</Label>
                          <select
                            id="db_type"
                            name="app[db_type]"
                            defaultValue={app.db_type || ""}
                            className="border-input bg-background ring-offset-background focus-visible:ring-ring flex h-10 w-full rounded-md border px-3 py-2 text-sm focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none"
                          >
                            <option value="">None</option>
                            <option value="postgresql">PostgreSQL</option>
                            <option value="mysql">MySQL</option>
                            <option value="sqlite">SQLite</option>
                          </select>
                          <InputError messages={errors["app.db_type"]} />
                        </div>

                        <div className="grid gap-2">
                          <Label htmlFor="db_name">Database Name</Label>
                          <Input
                            id="db_name"
                            name="app[db_name]"
                            type="text"
                            defaultValue={app.db_name || ""}
                            placeholder="myapp_production"
                          />
                          <InputError messages={errors["app.db_name"]} />
                        </div>
                      </div>
                    </>
                  )}

                  <div className="grid gap-2">
                    <Label htmlFor="notes">Notes (Markdown)</Label>
                    <textarea
                      id="notes"
                      name="app[notes]"
                      rows={5}
                      defaultValue={app.notes || ""}
                      className="border-input bg-background ring-offset-background placeholder:text-muted-foreground focus-visible:ring-ring flex min-h-[80px] w-full rounded-md border px-3 py-2 text-sm focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50"
                      placeholder="Application notes and documentation..."
                    />
                    <InputError messages={errors["app.notes"]} />
                  </div>

                  <div className="grid gap-2">
                    <Label htmlFor="notification_recipients">
                      Notification Recipients
                    </Label>
                    <Input
                      id="notification_recipients"
                      name="app[notification_recipients]"
                      type="text"
                      defaultValue={app.notification_recipients || ""}
                      placeholder="admin@you.digital, ops@you.digital"
                    />
                    <p className="text-muted-foreground text-xs">
                      Comma-separated email addresses for backup and SSL alerts.
                      Leave empty to notify all users.
                    </p>
                    <InputError
                      messages={errors["app.notification_recipients"]}
                    />
                  </div>

                  <div className="flex justify-between">
                    <Button
                      type="button"
                      variant="destructive"
                      onClick={handleDelete}
                    >
                      <Trash2 className="mr-2 h-4 w-4" />
                      Delete App
                    </Button>
                    <div className="flex gap-4">
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => history.back()}
                      >
                        Cancel
                      </Button>
                      <Button type="submit" disabled={processing}>
                        {processing && <Spinner />}
                        Save Changes
                      </Button>
                    </div>
                  </div>
                </>
              )}
            </Form>
          </CardContent>
        </Card>

        <Card className="mt-6 max-w-2xl">
          <CardHeader>
            <CardTitle>SEO Monitoring</CardTitle>
            <CardDescription>
              Configure automated SEO health checks for this application
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Form method="put" action={appPath(app.id)} className="space-y-6">
              {({ processing }) => (
                <>
                  <input type="hidden" name="app[seo_config][enabled]" value={seoEnabled ? "true" : "false"} />
                  <input type="hidden" name="app[seo_config][severity]" value={seoSeverity} />
                  {Object.entries(seoChecks).map(([key, value]) => (
                    <input key={key} type="hidden" name={`app[seo_config][checks][${key}]`} value={value ? "true" : "false"} />
                  ))}

                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="seo_enabled"
                      checked={seoEnabled}
                      onCheckedChange={(checked) => setSeoEnabled(checked === true)}
                    />
                    <Label htmlFor="seo_enabled" className="font-medium">
                      Enable SEO Monitoring
                    </Label>
                  </div>

                  {seoEnabled && (
                    <>
                      <div className="grid gap-2">
                        <Label>Issue Severity</Label>
                        <div className="space-y-2">
                          <div className="flex items-center space-x-2">
                            <input
                              type="radio"
                              id="severity_warning"
                              name="severity_radio"
                              value="warning"
                              checked={seoSeverity === "warning"}
                              onChange={(e) => setSeoSeverity(e.target.value)}
                              className="h-4 w-4"
                            />
                            <Label htmlFor="severity_warning" className="font-normal">
                              Warning - Report issues as warnings
                            </Label>
                          </div>
                          <div className="flex items-center space-x-2">
                            <input
                              type="radio"
                              id="severity_error"
                              name="severity_radio"
                              value="error"
                              checked={seoSeverity === "error"}
                              onChange={(e) => setSeoSeverity(e.target.value)}
                              className="h-4 w-4"
                            />
                            <Label htmlFor="severity_error" className="font-normal">
                              Error - Report all issues as errors
                            </Label>
                          </div>
                        </div>
                      </div>

                      <div className="grid gap-4">
                        <Label className="text-base">Enabled Checks</Label>
                        
                        <div className="space-y-3">
                          <div className="flex items-start space-x-2">
                            <Checkbox
                              id="check_robots_txt"
                              checked={seoChecks.robots_txt}
                              onCheckedChange={(checked) => setSeoChecks({...seoChecks, robots_txt: checked === true})}
                            />
                            <div className="grid gap-1">
                              <Label htmlFor="check_robots_txt" className="font-normal">
                                robots.txt restrictions
                              </Label>
                              <p className="text-muted-foreground text-xs">
                                Check for overly restrictive Disallow rules
                              </p>
                            </div>
                          </div>

                          <div className="flex items-start space-x-2">
                            <Checkbox
                              id="check_meta_robots"
                              checked={seoChecks.meta_robots}
                              onCheckedChange={(checked) => setSeoChecks({...seoChecks, meta_robots: checked === true})}
                            />
                            <div className="grid gap-1">
                              <Label htmlFor="check_meta_robots" className="font-normal">
                                Meta robots tags
                              </Label>
                              <p className="text-muted-foreground text-xs">
                                Detect noindex/nofollow meta tags
                              </p>
                            </div>
                          </div>

                          <div className="flex items-start space-x-2">
                            <Checkbox
                              id="check_x_robots_tag"
                              checked={seoChecks.x_robots_tag}
                              onCheckedChange={(checked) => setSeoChecks({...seoChecks, x_robots_tag: checked === true})}
                            />
                            <div className="grid gap-1">
                              <Label htmlFor="check_x_robots_tag" className="font-normal">
                                X-Robots-Tag headers
                              </Label>
                              <p className="text-muted-foreground text-xs">
                                Check HTTP headers for robots directives
                              </p>
                            </div>
                          </div>

                          <div className="flex items-start space-x-2">
                            <Checkbox
                              id="check_title"
                              checked={seoChecks.title}
                              onCheckedChange={(checked) => setSeoChecks({...seoChecks, title: checked === true})}
                            />
                            <div className="grid gap-1">
                              <Label htmlFor="check_title" className="font-normal">
                                Title tag
                              </Label>
                              <p className="text-muted-foreground text-xs">
                                Verify title presence and length (30-60 chars)
                              </p>
                            </div>
                          </div>

                          <div className="flex items-start space-x-2">
                            <Checkbox
                              id="check_meta_description"
                              checked={seoChecks.meta_description}
                              onCheckedChange={(checked) => setSeoChecks({...seoChecks, meta_description: checked === true})}
                            />
                            <div className="grid gap-1">
                              <Label htmlFor="check_meta_description" className="font-normal">
                                Meta description
                              </Label>
                              <p className="text-muted-foreground text-xs">
                                Check description presence and length (120-160 chars)
                              </p>
                            </div>
                          </div>

                          <div className="flex items-start space-x-2">
                            <Checkbox
                              id="check_canonical"
                              checked={seoChecks.canonical}
                              onCheckedChange={(checked) => setSeoChecks({...seoChecks, canonical: checked === true})}
                            />
                            <div className="grid gap-1">
                              <Label htmlFor="check_canonical" className="font-normal">
                                Canonical URL
                              </Label>
                              <p className="text-muted-foreground text-xs">
                                Verify canonical link tag presence
                              </p>
                            </div>
                          </div>

                          <div className="flex items-start space-x-2">
                            <Checkbox
                              id="check_open_graph"
                              checked={seoChecks.open_graph}
                              onCheckedChange={(checked) => setSeoChecks({...seoChecks, open_graph: checked === true})}
                            />
                            <div className="grid gap-1">
                              <Label htmlFor="check_open_graph" className="font-normal">
                                Open Graph tags
                              </Label>
                              <p className="text-muted-foreground text-xs">
                                Check for og:title, og:description, og:image
                              </p>
                            </div>
                          </div>

                          <div className="flex items-start space-x-2">
                            <Checkbox
                              id="check_sitemap"
                              checked={seoChecks.sitemap}
                              onCheckedChange={(checked) => setSeoChecks({...seoChecks, sitemap: checked === true})}
                            />
                            <div className="grid gap-1">
                              <Label htmlFor="check_sitemap" className="font-normal">
                                Sitemap presence
                              </Label>
                              <p className="text-muted-foreground text-xs">
                                Check if sitemap.xml exists and is valid
                              </p>
                            </div>
                          </div>

                          <div className="flex items-start space-x-2">
                            <Checkbox
                              id="check_structured_data"
                              checked={seoChecks.structured_data}
                              onCheckedChange={(checked) => setSeoChecks({...seoChecks, structured_data: checked === true})}
                            />
                            <div className="grid gap-1">
                              <Label htmlFor="check_structured_data" className="font-normal">
                                Structured data
                              </Label>
                              <p className="text-muted-foreground text-xs">
                                Detect Schema.org JSON-LD markup
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </>
                  )}

                  <div className="flex justify-end gap-4">
                    <Button type="button" variant="outline" onClick={() => history.back()}>
                      Cancel
                    </Button>
                    <Button type="submit" disabled={processing}>
                      {processing && <Spinner />}
                      Save SEO Config
                    </Button>
                  </div>
                </>
              )}
            </Form>
          </CardContent>
        </Card>
      </div>
    </AppLayout>
  )
}
